import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { supabase } from '../supabaseClient';
import { notificationModel } from '../shared/notificationSettings';
import {
  homeownerChannel,
  agentChannel,
  AGENT_EMAIL_FIELDS,
  AGENT_PUSH_FIELDS,
} from '../shared/notificationDelivery';

const BRAND = '#59c4c4';
const BRAND_DEEP = '#0f766e';

const CHANNEL_KEYS = {
  task_reminders: { email: 'task_reminders_email', push: 'task_reminders_push' },
  project_updates: { email: 'project_updates_email', push: 'project_updates_push' },
  monthly_digest: { email: 'monthly_digest_email', push: 'monthly_digest_push' },
  report_requests: { email: 'report_requests_email', push: 'report_requests_push' },
};

export const HOMEOWNER_PREF_DEFAULTS = {
  email_enabled: true,
  task_reminders_email: true,
  task_reminders_push: true,
  project_updates_email: true,
  project_updates_push: true,
  monthly_digest_email: true,
  monthly_digest_push: false,
  report_requests_email: true,
  report_requests_push: true,
};

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ display: 'block', flexShrink: 0 }}>
      <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChannelSwitch({ label, checked, disabled, locked, onChange, controlName }) {
  const { isDark, colors } = useTheme();
  const track = checked
    ? (locked ? BRAND_DEEP : BRAND)
    : (isDark ? '#525252' : '#d1d5db');
  return (
    <label style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.35rem',
      minWidth: '52px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit',
    }}
    >
      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textSecondary, letterSpacing: '0.02em' }}>{label}</span>
      <span style={{ position: 'relative', width: '52px', height: '28px', display: 'block' }}>
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-label={controlName}
          onChange={disabled ? undefined : onChange}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '52px',
            height: '28px',
            margin: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
        <span aria-hidden="true" style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '28px',
          background: track,
          transition: 'background 0.2s ease',
        }}
        >
          <span style={{
            position: 'absolute',
            height: '20px',
            width: '20px',
            left: '4px',
            top: '4px',
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transform: checked ? 'translateX(24px)' : 'none',
            transition: 'transform 0.2s ease',
          }}
          />
        </span>
      </span>
    </label>
  );
}

function PreferenceRow({ item, pushOn, emailOn, emailDisabled, onToggle, isLast, tour }) {
  const { colors } = useTheme();
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.75rem 1rem',
      padding: '1rem 0',
      borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
    }}
    >
      <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
        <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text, marginBottom: '0.25rem' }}>{item.title}</div>
        <p data-tour={tour || undefined} style={{ margin: 0, color: colors.textSecondary, fontSize: '0.875rem', lineHeight: 1.45 }}>{item.description}</p>
        {item.lockedNote ? (
          <p style={{
            margin: '0.45rem 0 0',
            color: colors.textSecondary,
            fontSize: '0.875rem',
            lineHeight: 1.45,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
          }}
          >
            <LockIcon />
            <span>{item.lockedNote}</span>
          </p>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginLeft: 'auto', flex: '0 0 auto' }}>
        <ChannelSwitch
          label="Push"
          checked={pushOn}
          disabled={!!item.locked}
          locked={!!item.locked}
          controlName={item.locked ? `${item.title} push, always on` : `${item.title} push`}
          onChange={() => onToggle(item.id, 'push')}
        />
        <ChannelSwitch
          label="Email"
          checked={emailOn}
          disabled={emailDisabled || !!item.locked}
          locked={!!item.locked}
          controlName={item.locked ? `${item.title} email, always on` : `${item.title} email`}
          onChange={() => onToggle(item.id, 'email')}
        />
      </div>
    </div>
  );
}

function cardStyle(colors, isDark, isMobile) {
  return {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: isMobile ? '1.25rem' : '1rem',
    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.08)',
    padding: isMobile ? '0.35rem 1.25rem' : '0.5rem 1.5rem',
    boxSizing: 'border-box',
  };
}

function useNarrow() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  ));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

export function HomeownerNotificationSettings({ audience, preferences, onPatch }) {
  const { colors, isDark } = useTheme();
  const isMobile = useNarrow();
  const model = notificationModel(audience || 'sponsorless');
  const [local, setLocal] = useState(preferences);
  useEffect(() => {
    setLocal(preferences);
  }, [preferences]);

  if (!local) {
    return <p style={{ color: colors.textSecondary }}>Loading notification settings...</p>;
  }

  const masterOn = model.masterDisabled ? true : local.email_enabled !== false;
  const card = cardStyle(colors, isDark, isMobile);

  const toggle = (id, channel) => {
    const keys = CHANNEL_KEYS[id];
    if (!keys || model.masterDisabled) return;
    const current = homeownerChannel(local, id, channel);
    const patch = {};
    patch[keys[channel]] = !current;
    setLocal({ ...local, ...patch });
    if (onPatch) onPatch(patch);
  };

  return (
    <div data-notification-ready="homeowner" style={{ width: '100%', maxWidth: '760px' }}>
      <h2 style={{
        margin: '0 0 0.35rem',
        fontWeight: 300,
        letterSpacing: '0.05em',
        fontSize: isMobile ? '1.5rem' : '2rem',
        color: colors.text,
      }}
      >
        {model.title}
      </h2>
      <p style={{ margin: '0 0 1.25rem', color: colors.textSecondary, fontSize: '0.95rem', lineHeight: 1.45 }}>{model.intro}</p>
      <div style={{ ...card, padding: isMobile ? '1rem 1.25rem' : '1.1rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text, marginBottom: '0.25rem' }}>Email notifications</div>
            <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.875rem', lineHeight: 1.45 }}>
              {model.masterDisabled
                ? 'Billing notices are the only email on a read-only home, and they stay on.'
                : 'Turns email off for every category except billing notices. Push stays as you set it.'}
            </p>
          </div>
          <ChannelSwitch
            label="Email"
            checked={masterOn}
            disabled={!!model.masterDisabled}
            locked={!!model.masterDisabled}
            controlName="Email notifications"
            onChange={() => {
              const patch = { email_enabled: !masterOn };
              setLocal({ ...local, ...patch });
              if (onPatch) onPatch(patch);
            }}
          />
        </div>
      </div>
      {model.groups.map((group) => (
        <section key={group.id} style={{ ...card, marginBottom: '1rem' }} aria-label={group.title || model.title}>
          {group.items.map((item, index) => (
            <PreferenceRow
              key={item.id}
              item={item}
              pushOn={item.locked ? true : homeownerChannel(local, item.id, 'push')}
              emailOn={item.locked ? true : homeownerChannel(local, item.id, 'email')}
              emailDisabled={!!model.masterDisabled || !masterOn}
              onToggle={toggle}
              isLast={index === group.items.length - 1}
              tour={item.id === 'task_reminders' ? 'roundup' : ''}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function switchesFromAgent(prefs) {
  const channels = {};
  Object.keys(AGENT_EMAIL_FIELDS).forEach((kind) => {
    channels[kind] = {
      email: agentChannel(prefs || {}, kind, 'email'),
      push: agentChannel(prefs || {}, kind, 'push'),
    };
  });
  return channels;
}

export function AgentNotificationSettings({ profile, preview, initialPreferences }) {
  const { colors, isDark } = useTheme();
  const isMobile = useNarrow();
  const model = notificationModel('agent');
  const [channels, setChannels] = useState(() => switchesFromAgent(initialPreferences || {}));
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (preview) return undefined;
    if (!profile || !profile.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: loadError } = await supabase
          .from('email_preferences')
          .select('*')
          .eq('agent_id', profile.id)
          .maybeSingle();
        if (loadError) throw loadError;
        if (!cancelled) setChannels(switchesFromAgent(data || {}));
      } catch (loadFailure) {
        console.error('Error loading notification preferences:', loadFailure);
        if (!cancelled) setError('Error loading preferences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preview, profile]);

  const toggle = (id, channel) => {
    setMessage('');
    setChannels((prev) => ({
      ...prev,
      [id]: { ...prev[id], [channel]: !prev[id][channel] },
    }));
  };

  const save = async () => {
    if (preview) {
      setMessage('Notification settings saved.');
      return;
    }
    if (!profile || !profile.id) return;
    setSaving(true);
    setMessage('');
    setError('');
    const payload = { updated_at: new Date().toISOString() };
    Object.keys(AGENT_EMAIL_FIELDS).forEach((kind) => {
      payload[AGENT_EMAIL_FIELDS[kind]] = channels[kind].email;
      payload[AGENT_PUSH_FIELDS[kind]] = channels[kind].push;
    });
    try {
      const existing = await supabase
        .from('email_preferences')
        .select('id')
        .eq('agent_id', profile.id)
        .maybeSingle();
      if (existing.error) throw existing.error;
      const write = existing.data
        ? supabase.from('email_preferences').update(payload).eq('agent_id', profile.id)
        : supabase.from('email_preferences').insert([{ ...payload, agent_id: profile.id }]);
      const { error: saveError } = await write;
      if (saveError) throw saveError;
      setMessage('Notification settings saved.');
    } catch (saveFailure) {
      console.error('Error saving notification preferences:', saveFailure);
      setError('Error saving preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ color: colors.textSecondary }}>Loading notification settings...</p>;
  }

  const card = cardStyle(colors, isDark, isMobile);
  return (
    <div data-notification-ready="agent" style={{ width: '100%', maxWidth: '760px' }}>
      <h2 style={{
        margin: '0 0 0.35rem',
        fontWeight: 300,
        letterSpacing: '0.05em',
        fontSize: isMobile ? '1.5rem' : '2rem',
        color: colors.text,
      }}
      >
        {model.title}
      </h2>
      <p style={{ margin: '0 0 1.25rem', color: colors.textSecondary, fontSize: '0.95rem', lineHeight: 1.45 }}>{model.intro}</p>
      {model.groups.map((group) => (
        <section key={group.id} style={{ ...card, marginBottom: '1rem' }} aria-label={group.title}>
          <h3 style={{ margin: '0.85rem 0 0', fontSize: '1rem', fontWeight: 600, color: colors.text }}>{group.title}</h3>
          {group.items.map((item, index) => (
            <PreferenceRow
              key={item.id}
              item={item}
              pushOn={channels[item.id] ? channels[item.id].push : false}
              emailOn={channels[item.id] ? channels[item.id].email : true}
              emailDisabled={false}
              onToggle={toggle}
              isLast={index === group.items.length - 1}
            />
          ))}
        </section>
      ))}
      {message ? (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          margin: '0.5rem 0 12px',
          textAlign: 'center',
          fontWeight: 500,
          background: '#d1fae5',
          color: '#065f46',
          border: '1px solid #6ee7b7',
        }}
        >
          {message}
        </div>
      ) : null}
      {error ? (
        <p style={{ color: '#b91c1c', margin: '0.5rem 0' }}>{error}</p>
      ) : null}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        style={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #4ab3b3 100%)`,
          color: '#fff',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '0.5rem',
          cursor: saving ? 'wait' : 'pointer',
          fontWeight: 650,
          fontSize: '1rem',
          fontFamily: 'inherit',
          minHeight: '48px',
        }}
      >
        {saving ? 'Saving...' : 'Save settings'}
      </button>
    </div>
  );
}
